import React, { useEffect, useState, createElement } from 'react';
import { BellIcon, SparklesIcon, MoonIcon, SunIcon, XIcon, CreditCardIcon } from 'lucide-react';
import { useTheme } from './ThemeContext';
import { StoryPopup } from './StoryPopup';
interface HeaderProps {
  onAlertClick?: (paymentId: string) => void;
  cardShippedNotification?: boolean;
}
export function Header({
  onAlertClick,
  cardShippedNotification
}: HeaderProps) {
  const {
    isDarkMode,
    toggleDarkMode
  } = useTheme();
  const [showAlertsPopup, setShowAlertsPopup] = useState(false);
  const [showStoryAlert, setShowStoryAlert] = useState(false);
  const [showStoryPopup, setShowStoryPopup] = useState(false);
  const [showStoriesListPopup, setShowStoriesListPopup] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  // Initialize AudioContext on user interaction for mobile
  useEffect(() => {
    const initAudio = () => {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
    };
    // Add listeners for user interaction to enable audio on mobile
    document.addEventListener('touchstart', initAudio, {
      once: true
    });
    document.addEventListener('click', initAudio, {
      once: true
    });
    return () => {
      document.removeEventListener('touchstart', initAudio);
      document.removeEventListener('click', initAudio);
    };
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowStoryAlert(true);
      setShowBanner(true);
      // Auto-dismiss banner after 8 seconds
      setTimeout(() => {
        setShowBanner(false);
      }, 8000);
      // Play pleasant two-tone notification chime
      try {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        // Resume AudioContext if suspended (mobile requirement)
        if (audioContext.state === 'suspended') {
          audioContext.resume().then(() => {
            playNotificationSound(audioContext);
          });
        } else {
          playNotificationSound(audioContext);
        }
      } catch (error) {
        console.log('Audio playback not available:', error);
      }
    }, 10000);
    return () => clearTimeout(timer);
  }, []);
  const playNotificationSound = (audioContext: AudioContext) => {
    // Create a pleasant two-tone chime (C5 to E5 - major third interval)
    const playTone = (frequency: number, startTime: number, duration: number) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      // Smooth envelope for a pleasant bell-like sound
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(0.15, startTime + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };
    const now = audioContext.currentTime;
    playTone(523.25, now, 0.3); // C5
    playTone(659.25, now + 0.15, 0.4); // E5
  };
  const newlyDetectedPayments = [{
    id: 'new1',
    name: 'YouTube Premium',
    amount: 11.99,
    logo: 'https://logo.clearbit.com/youtube.com',
    firstDetected: 'May 20, 2024'
  }, {
    id: 'new2',
    name: 'ChatGPT Plus',
    amount: 20.0,
    logo: 'https://logo.clearbit.com/openai.com',
    firstDetected: 'May 15, 2024'
  }];
  const cardShippedAlert = cardShippedNotification ? {
    id: 'card-shipped',
    name: 'University of Cambridge Card',
    description: 'Your card has been shipped',
    logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/cqsXrRfNthMHdJAUhheviM/53697a95-dadf-4e56-bfca-d3336b4e2b78-removebg-preview.png',
    timestamp: 'Just now'
  } : null;
  const allAlerts = cardShippedAlert ? [cardShippedAlert, ...newlyDetectedPayments] : newlyDetectedPayments;
  const totalAlertCount = allAlerts.length;
  const storyAlerts = [{
    id: 'story1',
    title: 'Wildlife Discovery',
    description: 'A rare dormouse nest was found in your supported habitat',
    charity: 'National Trust',
    image: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&q=80',
    timestamp: 'Just now'
  }];
  const handlePaymentClick = (paymentId: string) => {
    setShowAlertsPopup(false);
    if (onAlertClick) {
      onAlertClick(paymentId);
    }
  };
  const handleStoryIconClick = () => {
    if (showStoryAlert) {
      setShowStoriesListPopup(true);
    }
  };
  const handleStoryAlertClick = (storyId: string) => {
    setShowStoriesListPopup(false);
    setShowStoryPopup(true);
  };
  const handleBannerClick = () => {
    setShowBanner(false);
    setShowStoriesListPopup(true);
  };
  const handleDismissBanner = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowBanner(false);
  };
  return <>
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 py-4 px-6 flex items-center justify-between sticky top-0 z-10 transition-colors duration-200" data-id="element-389">
        <div className="flex items-center" data-id="element-390">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/9DSR6fPv4RouiZgYvtqGmX/Untitled-4-Photoroom.png" alt="Cauri Logo" className="h-8" data-id="element-391" />
        </div>
        <div className="flex items-center space-x-4" data-id="element-392">
          <button onClick={toggleDarkMode} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200" aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'} data-id="element-393">
            {isDarkMode ? <SunIcon className="w-6 h-6 text-gray-600 dark:text-gray-300" data-id="element-394" /> : <MoonIcon className="w-6 h-6 text-gray-600" data-id="element-395" />}
          </button>
          <div className="relative" data-id="element-396">
            <button onClick={() => setShowAlertsPopup(!showAlertsPopup)} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 relative transition-colors duration-200" data-id="element-397">
              <BellIcon className="w-6 h-6 text-gray-600 dark:text-gray-300" data-id="element-398" />
              {totalAlertCount > 0 && <span className="absolute top-0 right-0 bg-secondary text-white text-xs rounded-full w-4 h-4 flex items-center justify-center" data-id="element-399">
                  {totalAlertCount}
                </span>}
            </button>
            {showAlertsPopup && <>
                <div className="fixed inset-0 z-40" onClick={() => setShowAlertsPopup(false)} data-id="element-400" />
                <div className="fixed md:absolute left-4 right-4 md:left-auto md:right-0 top-16 md:top-auto md:mt-2 w-auto md:w-80 max-w-md mx-auto md:mx-0 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 z-50" data-id="element-401">
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center" data-id="element-402">
                    <div className="flex items-center" data-id="element-403">
                      <SparklesIcon className="w-5 h-5 text-secondary mr-2" data-id="element-404" />
                      <h3 className="font-semibold text-text-primary dark:text-white" data-id="element-405">
                        Alerts
                      </h3>
                    </div>
                    <button onClick={() => setShowAlertsPopup(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-406">
                      <XIcon className="w-5 h-5" data-id="element-407" />
                    </button>
                  </div>
                  <div className="max-h-96 overflow-y-auto" data-id="element-408">
                    {allAlerts.length > 0 ? <div className="p-2" data-id="element-409">
                        {cardShippedAlert && <div className="mb-3" data-id="element-410">
                            <div className="px-3 py-2" data-id="element-411">
                              <p className="text-xs font-medium text-green-600 dark:text-green-500" data-id="element-412">
                                Card Shipped
                              </p>
                            </div>
                            <div className="space-y-1" data-id="element-413">
                              <div className="w-full p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800" data-id="element-414">
                                <div className="flex items-center" data-id="element-415">
                                  <div className="w-10 h-10 bg-white rounded-full mr-3 flex items-center justify-center p-1" data-id="element-416">
                                    <CreditCardIcon className="w-6 h-6 text-primary" data-id="element-417" />
                                  </div>
                                  <div className="flex-1 min-w-0" data-id="element-418">
                                    <p className="font-medium text-sm text-text-primary dark:text-white" data-id="element-419">
                                      {cardShippedAlert.name}
                                    </p>
                                    <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-420">
                                      {cardShippedAlert.description}
                                    </p>
                                  </div>
                                  <div className="text-right ml-2 flex-shrink-0" data-id="element-421">
                                    <p className="text-xs font-medium text-green-600 dark:text-green-500" data-id="element-422">
                                      {cardShippedAlert.timestamp}
                                    </p>
                                  </div>
                                </div>
                                <div className="mt-2 pt-2 border-t border-green-200 dark:border-green-700" data-id="element-423">
                                  <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-424">
                                    Your card will arrive in 3-5 business days
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>}
                        {newlyDetectedPayments.length > 0 && <>
                            <div className="px-3 py-2" data-id="element-425">
                              <p className="text-xs font-medium text-primary dark:text-primary" data-id="element-426">
                                Newly Detected Payments
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-427">
                                We've automatically added these recurring
                                payments to your tracker
                              </p>
                            </div>
                            <div className="space-y-1 mt-2" data-id="element-428">
                              {newlyDetectedPayments.map(payment => <button key={payment.id} onClick={() => handlePaymentClick(payment.id)} className="w-full p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors text-left" data-id="element-429">
                                  <div className="flex items-center" data-id="element-430">
                                    <img src={payment.logo} alt={payment.name} className="w-10 h-10 rounded-full mr-3 object-contain bg-gray-100 dark:bg-gray-600 p-1" onError={e => {
                            e.currentTarget.src = `https://via.placeholder.com/40/754acd/FFFFFF?text=${payment.name.charAt(0)}`;
                          }} data-id="element-431" />
                                    <div className="flex-1 min-w-0" data-id="element-432">
                                      <p className="font-medium text-sm text-text-primary dark:text-white truncate" data-id="element-433">
                                        {payment.name}
                                      </p>
                                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-434">
                                        £{payment.amount.toFixed(2)}/month
                                      </p>
                                    </div>
                                    <div className="text-right ml-2 flex-shrink-0" data-id="element-435">
                                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-436">
                                        Detected
                                      </p>
                                      <p className="text-xs font-medium text-secondary dark:text-secondary" data-id="element-437">
                                        {payment.firstDetected}
                                      </p>
                                    </div>
                                  </div>
                                </button>)}
                            </div>
                          </>}
                      </div> : <div className="p-8 text-center" data-id="element-438">
                        <BellIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" data-id="element-439" />
                        <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-440">
                          No new alerts
                        </p>
                      </div>}
                  </div>
                </div>
              </>}
          </div>
          <div className="relative" data-id="element-441">
            <button onClick={handleStoryIconClick} disabled={!showStoryAlert} className={`p-2 rounded-full relative transition-colors duration-200 ${showStoryAlert ? 'hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer' : 'opacity-40 cursor-not-allowed'}`} data-id="element-442">
              <SparklesIcon className="w-6 h-6 text-gray-600 dark:text-gray-300" data-id="element-443" />
              {showStoryAlert && <span className="absolute top-0 right-0 bg-secondary text-white text-xs rounded-full w-4 h-4 flex items-center justify-center" data-id="element-444">
                  {storyAlerts.length}
                </span>}
            </button>
            {showStoriesListPopup && <>
                <div className="fixed inset-0 z-40" onClick={() => setShowStoriesListPopup(false)} data-id="element-445" />
                <div className="fixed md:absolute left-4 right-4 md:left-auto md:right-0 top-16 md:top-auto md:mt-2 w-auto md:w-80 max-w-md mx-auto md:mx-0 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 z-50" data-id="element-446">
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center" data-id="element-447">
                    <div className="flex items-center" data-id="element-448">
                      <SparklesIcon className="w-5 h-5 text-secondary mr-2" data-id="element-449" />
                      <h3 className="font-semibold text-text-primary dark:text-white" data-id="element-450">
                        Impact Stories
                      </h3>
                    </div>
                    <button onClick={() => setShowStoriesListPopup(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-451">
                      <XIcon className="w-5 h-5" data-id="element-452" />
                    </button>
                  </div>
                  <div className="max-h-96 overflow-y-auto" data-id="element-453">
                    <div className="p-2" data-id="element-454">
                      <div className="px-3 py-2" data-id="element-455">
                        <p className="text-xs font-medium text-primary dark:text-primary" data-id="element-456">
                          Your Impact
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-457">
                          See the real-world impact of your contributions
                        </p>
                      </div>
                      <div className="space-y-1 mt-2" data-id="element-458">
                        {storyAlerts.map(story => <button key={story.id} onClick={() => handleStoryAlertClick(story.id)} className="w-full p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors text-left" data-id="element-459">
                            <div className="flex items-start" data-id="element-460">
                              <img src={story.image} alt={story.title} className="w-16 h-16 rounded-lg mr-3 object-cover" data-id="element-461" />
                              <div className="flex-1 min-w-0" data-id="element-462">
                                <p className="font-medium text-sm text-text-primary dark:text-white" data-id="element-463">
                                  {story.title}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2" data-id="element-464">
                                  {story.description}
                                </p>
                                <div className="flex items-center justify-between mt-2" data-id="element-465">
                                  <p className="text-xs font-medium text-primary dark:text-primary" data-id="element-466">
                                    {story.charity}
                                  </p>
                                  <p className="text-xs text-gray-400" data-id="element-467">
                                    {story.timestamp}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </button>)}
                      </div>
                    </div>
                  </div>
                </div>
              </>}
          </div>
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-primary to-secondary flex items-center justify-center text-white font-bold" data-id="element-468">
            JD
          </div>
        </div>
      </header>
      {/* Story Alert Banner */}
      {showBanner && <div className="fixed top-16 left-0 right-0 z-50 animate-slide-down" style={{
      animation: 'slideDown 0.3s ease-out'
    }} data-id="element-469">
          <div className="mx-4 mt-2" data-id="element-470">
            <button onClick={handleBannerClick} className="w-full bg-white text-primary rounded-xl shadow-lg p-4 hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02] border border-gray-100" data-id="element-471">
              <div className="flex items-center justify-between" data-id="element-472">
                <div className="flex items-start flex-1" data-id="element-473">
                  <div className="w-12 h-12 bg-gradient-to-r from-primary to-secondary rounded-lg flex items-center justify-center mr-3 flex-shrink-0 p-2" data-id="element-474">
                    <img src="https://logos-world.net/wp-content/uploads/2021/03/National-Trust-Logo.png" alt="National Trust" className="w-full h-full object-contain" onError={e => {
                  e.currentTarget.style.display = 'none';
                  const sparkles = document.createElement('div');
                  sparkles.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/><path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/></svg>';
                  e.currentTarget.parentElement?.appendChild(sparkles.firstChild!);
                }} data-id="element-475" />
                  </div>
                  <div className="text-left flex-1" data-id="element-476">
                    <p className="font-semibold text-primary mb-1" data-id="element-477">
                      Wildlife Discovery!
                    </p>
                    <p className="text-sm text-gray-600" data-id="element-478">
                      A rare dormouse nest was found in your supported habitat
                    </p>
                  </div>
                </div>
                <button onClick={handleDismissBanner} className="ml-3 p-1 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0" data-id="element-479">
                  <XIcon className="w-5 h-5 text-gray-400 hover:text-gray-600" data-id="element-480" />
                </button>
              </div>
            </button>
          </div>
        </div>}
      <style data-id="element-481">{`
        @keyframes slideDown {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
      {showStoryPopup && <StoryPopup onClose={() => setShowStoryPopup(false)} data-id="element-482" />}
    </>;
}